/**
 * The static badlands the dashboard sits in: a seeded star field and three
 * ridgelines with flat-topped buttes, as SVG geometry. Pure and deterministic,
 * so the backdrop and the scene's poster draw the same land on every visit.
 */

export interface Star {
  x: number
  y: number
  r: number
  o: number
  /** The brightest few get a four-point glint. */
  glint: boolean
}

export interface Fire {
  x: number
  y: number
  r: number
}

export interface Landscape {
  width: number
  height: number
  /** SVG paths, closed below the box. */
  ridges: { far: string; mid: string; near: string }
  /** The same silhouettes as flat x, y pairs (for the scene's triangle strips). */
  lines: { far: number[]; mid: number[]; near: number[] }
  fires: Fire[]
}

/** mulberry32: small, fast, good enough for scenery. */
export function seeded(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function valueNoise(random: () => number, count: number) {
  const knots = Array.from({ length: count + 1 }, random)
  return (t: number) => {
    const x = Math.max(0, Math.min(0.999999, t)) * count
    const i = Math.floor(x)
    const f = x - i
    const s = f * f * (3 - 2 * f)
    return knots[i] * (1 - s) + knots[i + 1] * s
  }
}

/** A star field in a 1000 x 600 box, denser toward the top. */
export function starField(count = 150, seed = 7): Star[] {
  const random = seeded(seed)
  return Array.from({ length: count }, () => {
    const y = Math.pow(random(), 1.6) * 600
    const bright = random()
    return {
      x: Math.round(random() * 10000) / 10,
      y: Math.round(y * 10) / 10,
      r: Math.round((0.35 + bright * bright * 1.05) * 100) / 100,
      o: Math.round((0.22 + bright * 0.7) * 100) / 100,
      glint: bright > 0.965,
    }
  })
}

interface Butte {
  center: number
  half: number
  rise: number
}

function ridge(random: () => number, width: number, base: number, amplitude: number, buttes: Butte[], roughness: number) {
  const broad = valueNoise(random, 7)
  const fine = valueNoise(random, 41)
  const step = 6
  const points: string[] = []
  const line: number[] = []
  for (let x = 0; x <= width; x += step) {
    const t = x / width
    let y = base - amplitude * (0.62 * broad(t) + roughness * fine(t))
    for (const butte of buttes) {
      // Flat top, steep sides with a little talus at the foot.
      const d = Math.abs(x - butte.center) - butte.half
      const side = d <= 0 ? 1 : Math.max(0, 1 - d / (butte.half * 0.22 + 10))
      const lift = butte.rise * Math.pow(side, 0.55)
      y = Math.min(y, base - lift - (d <= 0 ? amplitude * 0.05 * fine(t * 3) : 0))
    }
    const rounded = Math.round(y * 10) / 10
    points.push(`${x},${rounded}`)
    line.push(x, rounded)
  }
  return { path: `M0,${base + 200} L${points.join(' L')} L${width},${base + 200} Z`, line }
}

/** The silhouette's height at `x` (box units), read off a ridge line. */
export function heightAt(line: number[], x: number): number {
  const step = line[2] - line[0]
  const i = Math.max(0, Math.min(line.length / 2 - 2, Math.floor((x - line[0]) / step)))
  const t = Math.max(0, Math.min(1, (x - line[i * 2]) / step))
  return line[i * 2 + 1] * (1 - t) + line[i * 2 + 3] * t
}

/**
 * Fires on the plain between the far ridge and the middle one: one per agent
 * on the front page, a scatter of them here. Each sits where the far land
 * shows above the middle ridge, so none floats in the sky.
 */
export function valleyFires(random: () => number, far: number[], mid: number[], count: number, width = 1440): Fire[] {
  const fires: Fire[] = []
  for (let index = 0; index < count; index++) {
    const x = 40 + (index + random()) * (width - 80) / count
    const top = heightAt(far, x) + 3
    const bottom = heightAt(mid, x) + 2
    const depth = random()
    if (bottom - top < 3) continue
    fires.push({ x: Math.round(x * 10) / 10, y: Math.round((top + depth * (bottom - top) * 0.85) * 10) / 10, r: Math.round((0.7 + depth * 1.3) * 10) / 10 })
  }
  return fires
}

/** Three ridgelines (far to near) in a 1440 x 320 box, and the fires on the plain. */
export function landscape(seed = 11): Landscape {
  const random = seeded(seed)
  const width = 1440
  const far = ridge(random, width, 238, 46, [
    { center: 270, half: 92, rise: 74 },
    { center: 1010, half: 120, rise: 86 },
    { center: 1290, half: 46, rise: 58 },
  ], 0.18)
  const mid = ridge(random, width, 268, 34, [
    { center: 560, half: 70, rise: 58 },
    { center: 1180, half: 34, rise: 44 },
  ], 0.26)
  const near = ridge(random, width, 300, 22, [{ center: 80, half: 120, rise: 34 }], 0.32)
  const fires = valleyFires(random, far.line, mid.line, 18)
  return {
    width,
    height: 320,
    ridges: { far: far.path, mid: mid.path, near: near.path },
    lines: { far: far.line, mid: mid.line, near: near.line },
    fires,
  }
}
